// FREEZE: YES
// Reason: Core API service - Centralized API communication layer with auth handling
// Date: 2024-01-15
// Author: Admin

/**
 * API Service 
 * 
 * Servicio centralizado para hacer llamadas a la API con manejo automático de errores
 * de autorización y reintento de autenticación.
 */

import { auth } from "../config/firebase";
import { config } from "../config";
import { autoTransformDates } from "../utils/timezone-interceptor";
import { UserSessionRegistry } from "../stores/user-session.registry";
import { emitAuthFailure } from "./authEvents";
import { debugLog, isCluDebugEnabled } from "../utils/debugLog";
import { isPublicRoute } from "../utils/publicRoutes";
import { recordAuthEvent, redactPath } from "../utils/authJournal";

const baseURL = config.apiUrl;

/**
 * Check if a JWT token is expiring within the given margin (default 5 minutes).
 * Returns true if token will expire soon or is already expired.
 */
function isTokenExpiringSoon(token, marginMs = 5 * 60 * 1000) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiresAt = (payload.exp || 0) * 1000;
    return expiresAt - Date.now() < marginMs;
  } catch {
    return true;
  }
}

// ─── Transport deadline ──────────────────────────────────────────────────────
//
// Production, 2026-10-05 18:41 UTC: the Conversations detail panel spun forever
// for an operator although the backend logged 200 in 0.05s for every transcript
// he opened. Cloud Run was scaling bella-4data-backend in and out, and an
// instance shutting down cuts the response it is still writing ("Truncated
// response body" — three of them were GET /api/admin/conversations/{id}). The
// old timeout raced fetch() only, which settles on the headers; response.text()
// ran with no deadline, so a body that never finished left the promise — and
// the spinner — pending forever. A body that did close short was parsed into
// `{}` by the JSON fallback and returned as success.
//
// One AbortController now bounds headers and body together, a non-empty JSON
// body that does not parse on a 2xx is an error, and an idempotent GET gets one
// more attempt when the failure was transport (network or truncated body) —
// the second attempt lands on a healthy instance. Timeouts and HTTP statuses
// are never retried here.

const GET_TRANSPORT_ATTEMPTS = 2;

function transportError(kind, message, cause) {
  const error = new Error(message);
  error.transport = kind;
  if (cause) error.cause = cause;
  return error;
}

function isRetryableTransportError(error) {
  return error?.transport === 'network' || error?.transport === 'truncated';
}

export async function fetchWithDeadline(url, fetchOptions, timeoutMs) {
  const controller = new AbortController();
  const callerSignal = fetchOptions.signal;
  const onCallerAbort = () => controller.abort(callerSignal.reason);
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else callerSignal.addEventListener('abort', onCallerAbort, { once: true });
  }
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    const response = await fetch(url, { ...fetchOptions, signal: controller.signal });
    const bodyText = response.status === 204 ? '' : await response.text();
    return { response, bodyText };
  } catch (error) {
    if (timedOut) {
      throw transportError('timeout', `Request timeout after ${timeoutMs}ms - URL: ${url}`, error);
    }
    if (callerSignal?.aborted || error?.name === 'AbortError') {
      throw error;
    }
    throw transportError('network', error?.message || 'Network error', error);
  } finally {
    clearTimeout(timer);
    if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort);
  }
}

export function assertCompleteJsonBody(response, bodyText, url) {
  if (!response.ok || !bodyText) return;
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) return;
  try {
    JSON.parse(bodyText);
  } catch (parseError) {
    throw transportError(
      'truncated',
      `Incomplete JSON response (${bodyText.length} bytes) - URL: ${url}`,
      parseError,
    );
  }
}

// Lightweight in-flight deduplication and response cache for selected GET endpoints
const inFlightRequests = new Map(); // key: method|url -> Promise<any>
const responseCache = new Map();    // key: method|url -> { data, expiresAt }

/**
 * Invalidates the cache for a specific endpoint
 * @param {string} endpoint - The endpoint to invalidate (e.g., 'modules/all')
 */
export const invalidateApiCache = (endpoint) => {
  const cleanEndpoint = endpoint.startsWith('/api/') 
    ? endpoint.slice(5) 
    : endpoint.startsWith('api/') 
      ? endpoint.slice(4) 
      : endpoint;
  
  // Build all possible cache keys for this endpoint
  const baseURL = config.apiUrl;
  const url = `${baseURL}/api/${cleanEndpoint}`;
  const cacheKey = `GET|${url}`;
  
  // Delete from cache
  if (responseCache.has(cacheKey)) {
    responseCache.delete(cacheKey);
    console.log(`[api.service] Cache invalidated for: ${cleanEndpoint}`);
  }
  
  // Also clear any in-flight requests
  if (inFlightRequests.has(cacheKey)) {
    inFlightRequests.delete(cacheKey);
    console.log(`[api.service] In-flight request cleared for: ${cleanEndpoint}`);
  }
};

function isEndpointCacheable(cleanEndpoint) {
  // Whitelist only safe idempotent endpoints we call frequently
  // - agents/:id/connection-details
  // - modules/all
  // - notifications/unread-count (frequently polled, safe to cache/deduplicate)
  // - control-center/summary (frequently polled, safe to cache/deduplicate)
  // - users/:email/organizations-with-modules (2026-10-01 incident: a broken
  //   retry guard in AuthContext.tsx hit this endpoint ~1,800 times in one
  //   tab in ~40 minutes because nothing collapsed concurrent/rapid calls
  //   for the same email — unlike session/exchange, which already had its
  //   own in-flight dedup. The retry guard itself was fixed; this is the
  //   independent safety net so a future bug of the same shape can't do it
  //   again, same as the other frequently-polled entries above)
  if (!cleanEndpoint || typeof cleanEndpoint !== 'string') return false;
  if (cleanEndpoint === 'modules/all') return true;
  if (/^agents\/\d+\/connection-details$/.test(cleanEndpoint)) return true;
  if (cleanEndpoint === 'notifications/unread-count') return true;
  if (cleanEndpoint === 'control-center/summary') return true;
  if (/^users\/[^/]+\/organizations-with-modules$/.test(cleanEndpoint)) return true;
  return false;
}

function getTtlMs(cleanEndpoint) {
  if (cleanEndpoint === 'modules/all') return 5 * 60 * 1000; // 5 minutes
  if (/^agents\/\d+\/connection-details$/.test(cleanEndpoint)) return 90 * 1000; // 90 seconds
  if (cleanEndpoint === 'notifications/unread-count') return 5 * 1000; // 5 seconds - short cache for polling endpoints
  if (cleanEndpoint === 'control-center/summary') return 5 * 1000; // 5 seconds - short cache for polling endpoints
  if (/^users\/[^/]+\/organizations-with-modules$/.test(cleanEndpoint)) return 10 * 1000; // 10 seconds
  return 0;
}

let authFailureRedirectInProgress = false;

function buildApiError(status, detail, statusText = 'Request failed', endpoint = '') {
  const normalizedDetail = typeof detail === 'object' && detail !== null
    ? (detail.message || detail.error || JSON.stringify(detail))
    : (detail || statusText || 'Request failed');
  const error = new Error(`Error ${status}: ${normalizedDetail}`);
  error.status = status;
  error.detail = normalizedDetail;
  error.rawDetail = detail;
  error.endpoint = endpoint;
  return error;
}

function isAuthUnauthorizedError(error) {
  if (!error) return false;
  if (error.status === 401) return true;
  const message = String(error.message || '');
  return message.includes('Error 401') || message.includes('credenciales') || message.includes('Unauthorized');
}

/**
 * Endpoints that are meant to answer WITHOUT a session.
 *
 * This list is not a convenience — it is what keeps an anonymous visitor from
 * being bounced to the login screen by a client built for logged-in users.
 * Everything below skips the pre-request credential wait, the automatic
 * re-auth retry, and both redirect-to-login handlers.
 *
 * `billing/v1/public/` is the signed public invoice page (BILLING_COBRANZA_TAREAS.md
 * PASO 6). It exists precisely so a finance contact with no GRID account can
 * pay; routing it through the authenticated client sent that customer to
 * /firebase-login, where they were told their account was pending
 * authorization — for an account they never had. Confirmed in production
 * 2026-09-02 with invoice CLU-2026-038.
 */
const PUBLIC_ENDPOINT_PREFIXES = [
  'auth/status',
  'auth/user-info',
  'billing/v1/public/',
  // Token-scoped conversation ask card (AskCardRenderer): the token is the
  // authorization; a 401/403 there must never trigger a resync or a logout.
  'widget/survey-ask/',
  // Per-person survey respondent session (SurveyLandingPage): the session
  // token in the path is the authorization; visitors have no account, so a
  // 401/403 there must never trigger a resync or a logout.
  'widget/survey/',
  // Full-screen view of a WeBot widget artifact (ArtifactViewPage): the signed
  // token is the authorization; a viewer's own session is never sent.
  'widget/artifact-views/',
];

function isAuthPublicEndpoint(cleanEndpoint) {
  if (!cleanEndpoint) return false;
  return PUBLIC_ENDPOINT_PREFIXES.some((prefix) => cleanEndpoint.startsWith(prefix));
}

function hasSessionContext() {
  try {
    return !!auth.currentUser || !!localStorage.getItem('authData') || !!localStorage.getItem('user');
  } catch {
    return !!auth.currentUser;
  }
}

async function handlePersistentUnauthorized(cleanEndpoint, sourceError = null) {
  if (authFailureRedirectInProgress) return;
  if (isAuthPublicEndpoint(cleanEndpoint)) return;
  if (!hasSessionContext()) return;
  // Guard the PAGE, not only the endpoint. Exempting `billing/v1/public/` was
  // not enough on its own: the call that bounced a customer off his invoice was
  // a different, non-public one made while the public page was open.
  if (isPublicRoute()) return;

  authFailureRedirectInProgress = true;
  try {
    console.warn('[apiCall] Persistent 401 detected, clearing session and redirecting to login.', {
      endpoint: cleanEndpoint,
      sourceError: sourceError?.message,
    });

    try {
      UserSessionRegistry.clearCache();
    } catch (registryError) {
      console.warn('[apiCall] Could not clear UserSessionRegistry cache:', registryError);
    }

    try {
      localStorage.removeItem('authData');
      localStorage.removeItem('user');
      localStorage.removeItem('activeOrganization');
      localStorage.removeItem('selectedOrganizationId');
      localStorage.removeItem('localSessionEstablished');
      localStorage.removeItem('lastSyncTime');
      sessionStorage.removeItem('selectedOrganizationId');
    } catch (storageError) {
      console.warn('[apiCall] Could not fully clear local/session storage:', storageError);
    }

    try {
      if (auth.currentUser) {
        await auth.signOut();
      }
    } catch (signOutError) {
      console.warn('[apiCall] Firebase signOut failed during 401 handling:', signOutError);
    }

    if (typeof window !== 'undefined') {
      if (!isPublicRoute()) {
        recordAuthEvent('persistent-unauthorized', () => ({
          reason: redactPath(cleanEndpoint),
          user: !!auth.currentUser,
          detail: { status: sourceError?.status ?? null },
        }));
        window.location.href = '/firebase-login?session-expired=true';
      }
    }
  } finally {
    // Allow future redirects if user logs in again and later expires.
    setTimeout(() => {
      authFailureRedirectInProgress = false;
    }, 2000);
  }
}

/**
 * Mid-session access-state 403 handler: account_blocked / org_suspended.
 *
 * These are NOT stale-key/token problems (unlike the `inactive`/`expired` branch
 * above) — they mean the backend's resolve_user_access_state() has decided this
 * user must not keep using the app, discovered on a call made *after* login (e.g.
 * an admin blocks the account, or suspends the org, while the user is already
 * navigating). The login-time case is owned entirely by AuthContext's own catch
 * block around the `auth/gateway` call — that call passes
 * `disableAutoLogoutOn401: true`, which this function also respects, so it never
 * double-handles the same 403 the AuthContext catch is already about to process.
 *
 * Shares `authFailureRedirectInProgress` with handlePersistentUnauthorized so
 * parallel 403s (multiple in-flight requests hitting the same blocked/suspended
 * state) trigger exactly one sign-out + redirect cycle.
 */
async function handleAccessStateError(state, cleanEndpoint, sourceError = null) {
  if (authFailureRedirectInProgress) return;
  if (isAuthPublicEndpoint(cleanEndpoint)) return;
  // Same guard handlePersistentUnauthorized already had, and for the same
  // reason: someone with no session has nothing to sign out of, and sending
  // them to the login screen answers a question they never asked. This is
  // "mid-session access state" — without a session there is no mid.
  if (!hasSessionContext()) return;
  if (isPublicRoute()) return;

  authFailureRedirectInProgress = true;
  try {
    console.warn(`[apiCall] ${state} detected mid-session, clearing session and redirecting to login.`, {
      endpoint: cleanEndpoint,
      sourceError: sourceError?.message,
    });

    try {
      UserSessionRegistry.clearCache();
    } catch (registryError) {
      console.warn('[apiCall] Could not clear UserSessionRegistry cache:', registryError);
    }

    try {
      localStorage.removeItem('authData');
      localStorage.removeItem('user');
      localStorage.removeItem('activeOrganization');
      localStorage.removeItem('selectedOrganizationId');
      localStorage.removeItem('localSessionEstablished');
      localStorage.removeItem('lastSyncTime');
      sessionStorage.removeItem('selectedOrganizationId');
    } catch (storageError) {
      console.warn('[apiCall] Could not fully clear local/session storage:', storageError);
    }

    try {
      if (auth.currentUser) {
        await auth.signOut();
      }
    } catch (signOutError) {
      console.warn('[apiCall] Firebase signOut failed during access-state handling:', signOutError);
    }

    if (typeof window !== 'undefined') {
      if (!isPublicRoute()) {
        const param = state === 'account_blocked' ? 'account-blocked' : 'org-suspended';
        recordAuthEvent('access-state-redirect', () => ({
          reason: state,
          user: !!auth.currentUser,
          detail: { endpoint: redactPath(cleanEndpoint) },
        }));
        window.location.href = `/firebase-login?${param}=true`;
      }
    }
  } finally {
    setTimeout(() => {
      authFailureRedirectInProgress = false;
    }, 2000);
  }
}

/**
 * Realiza una llamada a la API con manejo automático de errores de autenticación
 * @param {string} endpoint - El endpoint a llamar (sin /api/)
 * @param {Object|string} options - Opciones para la llamada fetch, o método HTTP como string ('GET', 'POST', etc)
 * @param {Object} data - Datos a enviar (solo si options es un string representando el método)
 * @param {boolean} forceReauth - Si debe forzar la reautenticación antes de la llamada
 * @param {Object} authOptions - Opciones adicionales de autenticación {forceWaitAuth: true|false, isFormData: true|false, responseType: 'json'|'blob'|'text', silentError: true|false}
 * @param {boolean} authOptions.silentError - Si es true, no registra errores en consola (útil para errores esperados que se manejan silenciosamente)
 * @returns {Promise<any>} - La respuesta de la API
 */
export const apiCall = async (endpoint, options = {}, data = null, forceReauth = false, authOptions = {}) => {
  // Detectar si options es un string (método HTTP)
  if (typeof options === 'string') {
    // Convertir a formato de opciones
    const method = options.toUpperCase(); // Normalizar método
    const newOptions = {
      method,
      headers: {}
    };
    
    // Añadir body si hay datos y no es GET
    if (data !== null && method !== 'GET') {
      debugLog(`Preparando datos para enviar en método ${method} a ${endpoint}:`, data);
      
      // Verificar si es FormData (para uploads de archivos)
      if (authOptions.isFormData || data instanceof FormData) {
        newOptions.body = data;
        // No establecer Content-Type para FormData, el browser lo hace automáticamente
      } else {
        newOptions.body = JSON.stringify(data);
      }
    }
    
    // Reemplazar options con el objeto construido
    options = newOptions;
  }
  
  // Detect polling endpoints early for logging optimization
  const cleanEndpointForLogging = endpoint.startsWith('/api/') 
    ? endpoint.slice(5) 
    : endpoint.startsWith('api/') 
      ? endpoint.slice(4) 
      : endpoint;
  const isPollingEndpointEarly = cleanEndpointForLogging === 'notifications/unread-count' || cleanEndpointForLogging === 'control-center/summary';
  
  // Reduced logging for polling endpoints; general case gated behind the CLU debug flag (P4)
  if (!isPollingEndpointEarly || process.env.NODE_ENV === 'development') {
    debugLog(`Llamando a API: ${endpoint} con método ${options.method || 'GET'}`);
    if (options.body && isCluDebugEnabled()) {
      if (options.body instanceof FormData) {
        debugLog(`Body de la petición (FormData):`, options.body);
      } else {
        try {
          debugLog(`Body de la petición:`, JSON.parse(options.body));
        } catch (e) {
          debugLog(`Body de la petición (no es JSON):`, options.body);
        }
      }
    }
  }
  
  // Esperar explícitamente a que la autenticación de Firebase esté lista si se solicita
  if (authOptions.forceWaitAuth) {
    await waitForAuthentication();
  }
  
  // Primero detectar si hay un cambio de organización
  await detectOrganizationChange(endpoint, options);
  
  // Asegurarnos que el endpoint no empieza con /api/ duplicado
  const cleanEndpoint = endpoint.startsWith('/api/') 
    ? endpoint.slice(5) 
    : endpoint.startsWith('api/') 
      ? endpoint.slice(4) 
      : endpoint;
  
  // Construir la URL completa
  // Soporte para params en authOptions: { params: { a: 1, b: 'x' } }
  let url = `${baseURL}/api/${cleanEndpoint}`;
  if (authOptions && authOptions.params && typeof authOptions.params === 'object') {
    const usp = new URLSearchParams();
    Object.entries(authOptions.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        usp.append(key, String(value));
      }
    });
    const qs = usp.toString();
    if (qs) {
      url += (url.includes('?') ? '&' : '?') + qs;
    }
  }
  
  // Configuración predeterminada
  const defaultOptions = {
    method: options.method || 'GET',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
    }
  };
  
  // Solo añadir Content-Type si no es FormData
  // Check both options.body and authOptions.isFormData to detect FormData
  const isFormDataBody = options.body instanceof FormData;
  const isFormDataFlag = authOptions && authOptions.isFormData;
  const isFormDataRequest = isFormDataBody || isFormDataFlag;
  
  console.log(`[apiCall] 🔍 FormData detection: body=FormData? ${isFormDataBody}, flag=${isFormDataFlag}, final=${isFormDataRequest}`);
  
  if (!isFormDataRequest) {
    defaultOptions.headers['Content-Type'] = 'application/json';
  } else {
    console.log(`[apiCall] ✅ FormData detected - NOT setting Content-Type (browser will set it)`);
  }
  
  // Añadir datos (body) si existen
  if (options.body) {
    defaultOptions.body = options.body;
  }
  
  // Combinar opciones
  const fetchOptions = {
    ...defaultOptions
  };
  
  // Merge headers from options.headers if provided (e.g., X-Agent-ID)
  // IMPORTANT: Do NOT override Content-Type if it was already set (e.g., for FormData)
  if (options.headers && typeof options.headers === 'object') {
    // Remove Content-Type from options.headers if it exists and we're sending FormData
    const headersToMerge = { ...options.headers };
    if (isFormDataRequest && headersToMerge['Content-Type']) {
      delete headersToMerge['Content-Type'];
      console.log(`[apiCall] ⚠️ Removed Content-Type from headers (FormData detected)`);
    }
    Object.assign(fetchOptions.headers, headersToMerge);
    console.log(`[apiCall] 🔧 Mergeando headers de options:`, Object.keys(headersToMerge));
  }
  
  // ---- ESTRATEGIA: Autenticación centralizada usando UserSessionRegistry ----
  // Obtener headers de autenticación desde el registro centralizado
  try {
    // Obtener ID de organización seleccionada para pasarlo al registry
    let selectedOrgId = null;
    
    // 1. Intentar obtener de activeOrganization (formato usado por el componente Layout)
    try {
      const activeOrgData = localStorage.getItem('activeOrganization');
      if (activeOrgData) {
        const activeOrgJson = JSON.parse(activeOrgData);
        if (activeOrgJson && activeOrgJson.organization_id) {
          selectedOrgId = activeOrgJson.organization_id;
          console.log(`API: Usando organización de activeOrganization: ${selectedOrgId}`);
        }
      }
    } catch (e) {
      console.error("Error al obtener activeOrganization:", e);
    }
    
    // 2. Si no se encontró en activeOrganization, buscar en otras fuentes
    if (!selectedOrgId) {
      selectedOrgId = 
        localStorage.getItem('selectedOrganizationId') || 
        sessionStorage.getItem('selectedOrganizationId');
      
      if (selectedOrgId) {
        console.log(`API: Usando organización de storage: ${selectedOrgId}`);
      }
    }
    
    // Obtener headers de autenticación desde el registry centralizado
    console.log(`[apiCall] 🔑 Obteniendo authHeaders desde UserSessionRegistry...`);
    console.log(`[apiCall] 📋 orgId: ${selectedOrgId ? Number(selectedOrgId) : 'undefined'}`);
    const getHeadersStartTime = Date.now();
    
    const getHeadersTimeout = 15000; // 15 segundos para obtener headers
    const getHeadersPromise = UserSessionRegistry.getAuthHeaders({
      orgId: selectedOrgId ? Number(selectedOrgId) : undefined,
      includeToken: true,
    });
    const getHeadersTimeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`getAuthHeaders timeout after ${getHeadersTimeout}ms`)), getHeadersTimeout);
    });
    
    const authHeaders = await Promise.race([getHeadersPromise, getHeadersTimeoutPromise]);
    const getHeadersDuration = Date.now() - getHeadersStartTime;
    console.log(`[apiCall] ✅ authHeaders obtenidos en ${getHeadersDuration}ms`);
    
    // Proactive token refresh: if token is expiring soon, refresh before sending request
    const currentAuthToken = authHeaders['Authorization']?.replace('Bearer ', '');
    if (currentAuthToken && isTokenExpiringSoon(currentAuthToken)) {
      try {
        const firebaseUser = auth.currentUser;
        if (firebaseUser) {
          const freshToken = await firebaseUser.getIdToken(true);
          if (freshToken) {
            authHeaders['Authorization'] = `Bearer ${freshToken}`;
            // Update localStorage so other calls benefit too
            try {
              const ad = JSON.parse(localStorage.getItem('authData') || '{}');
              ad.token = freshToken;
              ad.last_updated = new Date().toISOString();
              localStorage.setItem('authData', JSON.stringify(ad));
            } catch { /* non-critical */ }
          }
        }
      } catch (refreshErr) {
        console.warn('[apiCall] Proactive token refresh failed, using current token:', refreshErr.message);
      }
    }

    // Apply auth headers to fetchOptions
    Object.assign(fetchOptions.headers, authHeaders);
    
    // Merge custom headers from authOptions (e.g., X-Agent-ID)
    // CRITICAL: This happens AFTER authHeaders are merged, so custom headers take precedence
    if (authOptions && authOptions.headers && typeof authOptions.headers === 'object') {
      const customHeaders = authOptions.headers;
      console.log(`[apiCall] 🔧 Mergeando custom headers desde authOptions:`, Object.keys(customHeaders));
      console.log(`[apiCall] 🔧 Custom headers values:`, customHeaders);
      
      // Merge custom headers into fetchOptions.headers
      Object.assign(fetchOptions.headers, customHeaders);
      
      // Verify X-Agent-ID is present after merge
      const xAgentId = fetchOptions.headers['X-Agent-ID'];
      console.log(`[apiCall] ✅ Headers después de merge custom:`, {
        'X-Agent-ID': xAgentId || '[MISSING]',
        'X-Agent-ID-type': typeof xAgentId,
        'X-User-Email': fetchOptions.headers['X-User-Email'] ? '[SET]' : '[MISSING]',
      });
      
      if (!xAgentId && customHeaders['X-Agent-ID']) {
        console.error(`[apiCall] ❌ ERROR: X-Agent-ID was in customHeaders but missing after merge!`);
        console.error(`[apiCall] fetchOptions.headers keys:`, Object.keys(fetchOptions.headers));
      }
    }
    
    console.log(`[apiCall] 📋 Headers de autenticación obtenidos desde UserSessionRegistry`, {
      'X-User-Email': authHeaders['X-User-Email'] ? '[SET]' : '[MISSING]',
      'X-User-Role': authHeaders['X-User-Role'] ? '[SET]' : '[MISSING]',
      'X-User-Org': authHeaders['X-User-Org'] ? '[SET]' : '[MISSING]',
      'Authorization': authHeaders['Authorization'] ? '[SET]' : '[MISSING]',
    });
  } catch (error) {
    console.error('='.repeat(80));
    console.error('[apiCall] ❌ CRITICAL: Could not use UserSessionRegistry');
    console.error('='.repeat(80));
    console.error('[apiCall] Error:', error);
    console.error('[apiCall] Error message:', error?.message);
    console.error('[apiCall] Error stack:', error?.stack);
    console.error('='.repeat(80));
    // NO FALLBACK to localStorage - UserSessionRegistry is required
    // Request may fail without proper auth headers
    console.warn('[apiCall] ⚠️ Proceeding without auth headers - request may fail');
  }
  
  // Verificar si tenemos alguna forma de autenticación
  const hasAuthToken = !!fetchOptions.headers['Authorization'] && 
                       fetchOptions.headers['Authorization'] !== '[MISSING]' &&
                       !fetchOptions.headers['Authorization'].startsWith('[MISSING]');
  const hasUserHeaders = !!fetchOptions.headers['X-User-Email'] && 
                         fetchOptions.headers['X-User-Email'] !== '[MISSING]' &&
                         !!fetchOptions.headers['X-User-Role'] && 
                         fetchOptions.headers['X-User-Role'] !== '[MISSING]' &&
                         !!fetchOptions.headers['X-User-Org'] &&
                         fetchOptions.headers['X-User-Org'] !== '[MISSING]';
  
  // MEJORA: Si no tenemos credenciales válidas, esperar a que AuthContext sincronice
  // Esto es especialmente importante después de login cuando puede haber una condición de carrera
  // A public endpoint must never enter the block below: with no Firebase user
  // it waits, gives up, and retries with forceReauth — a sequence built for a
  // logged-in user whose session is still syncing, not for a visitor who has
  // no account at all and never will.
  const requiresAuth = !isAuthPublicEndpoint(cleanEndpoint)
                       && !endpoint.includes('auth/') && !endpoint.includes('login') && !endpoint.includes('register');
  if (requiresAuth && !hasAuthToken && !hasUserHeaders && !forceReauth) {
    console.warn(`Llamada a ${url} sin credenciales suficientes. Esperando sincronización de AuthContext...`);
    
    // MEJORA: Esperar a que UserSessionRegistry tenga datos antes de reintentar
    // Esto evita loops infinitos cuando AuthContext aún no ha sincronizado
    let waitCount = 0;
    const maxWaitCount = 20; // Máximo 10 segundos (20 * 500ms)
    const waitDelay = 500; // Esperar 500ms entre verificaciones
    
    while (waitCount < maxWaitCount) {
      try {
        const { UserSessionRegistry } = await import('../stores/user-session.registry');
        const userData = await UserSessionRegistry.getCurrentUser();
        
        // Si tenemos email, intentar obtener headers de nuevo
        if (userData && userData.email) {
          console.log(`[apiCall] ✅ Datos de usuario disponibles después de ${waitCount * waitDelay}ms, reintentando...`);
          // Reintentar la llamada con forceReauth para sincronizar
          return apiCall(endpoint, options, data, true, { forceWaitAuth: true });
        }
        
        // Si no hay email pero hay Firebase user, esperar más
        if (userData && userData.firebaseUser && !userData.email) {
          console.log(`[apiCall] ⏳ Esperando sincronización de AuthContext (${waitCount + 1}/${maxWaitCount})...`);
          waitCount++;
          await new Promise(resolve => setTimeout(resolve, waitDelay));
          continue;
        }
        
        // Si no hay Firebase user, verificar si Firebase tiene usuario activo
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        if (!auth.currentUser) {
          // No hay usuario en Firebase, salir del loop
          console.warn(`[apiCall] ⚠️ No hay usuario en Firebase, no se puede autenticar`);
          break;
        }
        
        // Hay usuario en Firebase pero UserSessionRegistry no sincronizado aún
        console.log(`[apiCall] ⏳ Firebase tiene usuario pero UserSessionRegistry no sincronizado (${waitCount + 1}/${maxWaitCount})...`);
        waitCount++;
        await new Promise(resolve => setTimeout(resolve, waitDelay));
      } catch (waitError) {
        console.error(`[apiCall] ❌ Error esperando sincronización:`, waitError);
        break;
      }
    }
    
    // Si después de esperar aún no tenemos credenciales, intentar reautenticación
    console.warn(`Llamada a ${url} sin credenciales suficientes después de esperar. Intentando reautenticación...`);
    return apiCall(endpoint, options, data, true, { forceWaitAuth: true });
  }
  
  // Reduced logging for performance - only log in development or for non-polling endpoints
  const isPollingEndpoint = cleanEndpoint === 'notifications/unread-count' || cleanEndpoint === 'control-center/summary';
  if (!isPollingEndpoint || process.env.NODE_ENV === 'development') {
    console.log(`[apiCall] 🎯 ENTRANDO AL TRY PRINCIPAL`);
    console.log(`[apiCall] 📋 forceReauth: ${forceReauth}`);
    console.log(`[apiCall] 📋 endpoint: ${endpoint}`);
    console.log(`[apiCall] 📋 cleanEndpoint: ${cleanEndpoint}`);
    console.log(`[apiCall] 📋 url construida: ${url}`);
  }
  
  try {
    // Si se solicita reautenticación explícita, intentar sincronizar sesión primero
    if (forceReauth) {
      console.log(`[apiCall] 🔄 forceReauth=true, sincronizando sesión...`);
      const syncStartTime = Date.now();
      try {
        console.log(`[apiCall] ⏳ Llamando a forceSyncSession()...`);
        const syncOk = await forceSyncSession({
          forceTokenRefresh: !!authOptions.forceTokenRefresh,
        });
        const syncDuration = Date.now() - syncStartTime;
        if (!syncOk) {
          if (authOptions.strictForceReauth) {
            throw new Error("Authentication session unavailable. Please sign in again.");
          }
          console.warn(
            `[apiCall] ⚠️ forceSyncSession returned false after ${syncDuration}ms; continuing request and relying on backend 401 for definitive auth result.`
          );
        } else {
          console.log(`[apiCall] ✅ Sesión sincronizada en ${syncDuration}ms`);
          // CRITICAL: refresh auth headers after force sync so the request uses
          // the newly refreshed Firebase token instead of stale pre-sync headers.
          try {
            let refreshedOrgId = null;
            try {
              const activeOrgData = localStorage.getItem('activeOrganization');
              if (activeOrgData) {
                const activeOrgJson = JSON.parse(activeOrgData);
                if (activeOrgJson && activeOrgJson.organization_id) {
                  refreshedOrgId = activeOrgJson.organization_id;
                }
              }
            } catch {
              // ignore parse errors, fallback below
            }
            if (!refreshedOrgId) {
              refreshedOrgId = localStorage.getItem('selectedOrganizationId') || sessionStorage.getItem('selectedOrganizationId');
            }

            const refreshedHeaders = await UserSessionRegistry.getAuthHeaders({
              orgId: refreshedOrgId ? Number(refreshedOrgId) : undefined,
              includeToken: true,
            });
            Object.assign(fetchOptions.headers, refreshedHeaders);
            console.log('[apiCall] ✅ Auth headers refreshed after forceSyncSession');
          } catch (rehydrateErr) {
            console.warn('[apiCall] ⚠️ Could not refresh auth headers after sync:', rehydrateErr?.message || rehydrateErr);
          }
        }
      } catch (syncError) {
        const syncDuration = Date.now() - syncStartTime;
        if (!authOptions.strictForceReauth) {
          console.warn(
            `[apiCall] ⚠️ Error sincronizando sesión después de ${syncDuration}ms, pero continuando request para evitar falso positivo:`,
            syncError?.message || syncError
          );
        } else {
        console.error(`[apiCall] ❌ Error sincronizando sesión después de ${syncDuration}ms:`, syncError);
        console.error(`[apiCall] ❌ Sync error stack:`, syncError.stack);
        throw syncError;
        }
      }
    } else {
      console.log(`[apiCall] ⏭️  forceReauth=false, saltando sincronización`);
    }
    
    // Limpiar headers inválidos antes de la llamada
    if (!isPollingEndpoint || process.env.NODE_ENV === 'development') {
      console.log(`[apiCall] 🧹 Limpiando headers inválidos...`);
    }
    Object.keys(fetchOptions.headers).forEach(key => {
      if (fetchOptions.headers[key] === undefined || fetchOptions.headers[key] === null) {
        delete fetchOptions.headers[key];
      }
    });
    
    // Log de diagnóstico - mostrar headers sanitizados (only for non-polling endpoints or dev)
    if (!isPollingEndpoint || process.env.NODE_ENV === 'development') {
      console.log(`[apiCall] 📋 URL final: ${url}`);
      console.log(`[apiCall] 📋 Método: ${fetchOptions.method || 'GET'}`);
      console.log(`[apiCall] 📋 Headers sanitizados:`, {
        ...Object.keys(fetchOptions.headers).reduce((acc, key) => {
          acc[key] = key === 'Authorization' ? '[FILTERED]' : fetchOptions.headers[key];
          return acc;
        }, {})
      });
    }
    
    // Deduplication & cache (GET + whitelist)
    const effectiveMethod = (fetchOptions.method || 'GET').toUpperCase();
    const cacheable = effectiveMethod === 'GET' && isEndpointCacheable(cleanEndpoint);
    
    // For polling endpoints, normalize cache key to ignore optional params for better deduplication
    // This ensures that multiple components polling the same endpoint share the same request
    let cacheKey = `${effectiveMethod}|${url}`;
    if (cacheable && (cleanEndpoint === 'notifications/unread-count' || cleanEndpoint === 'control-center/summary')) {
      // For polling endpoints, use base URL without params for deduplication
      // Params are optional and don't significantly change the response for these endpoints
      const baseUrl = `${baseURL}/api/${cleanEndpoint}`;
      cacheKey = `${effectiveMethod}|${baseUrl}`;
    }
    
    if (cacheable) {
      const cached = responseCache.get(cacheKey);
      if (cached && cached.expiresAt > Date.now()) {
        return cached.data;
      }
      const inflight = inFlightRequests.get(cacheKey);
      if (inflight) {
        return await inflight;
      }
    }

    const doRequest = (async () => {
      // Realizar la llamada
      // Reduced logging for polling endpoints to avoid backend blocking
      if (!isPollingEndpoint || process.env.NODE_ENV === 'development') {
        console.log('='.repeat(80));
        console.log(`[apiCall] 🚀 Realizando fetch a ${url}`);
        console.log(`[apiCall] 📋 Método: ${fetchOptions.method || 'GET'}`);
        console.log(`[apiCall] 📦 Headers:`, Object.keys(fetchOptions.headers).reduce((acc, key) => {
          acc[key] = key === 'Authorization' ? '[FILTERED]' : fetchOptions.headers[key];
          return acc;
        }, {}));
        
        if (fetchOptions.body) {
          if (fetchOptions.body instanceof FormData) {
            console.log(`[apiCall] 📤 Enviando FormData`);
            // Entry values/filenames may carry PII or secrets (e.g. a password
            // field, an API key the user is configuring) — dev-only, never logged in prod.
            if (import.meta.env.DEV) {
              for (const [key, value] of fetchOptions.body.entries()) {
                if (value instanceof File) {
                  console.log(`[apiCall]   - ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
                } else {
                  console.log(`[apiCall]   - ${key}: ${value}`);
                }
              }
            }
          } else if (import.meta.env.DEV) {
            // Full request body can contain secrets (passwords, API keys) —
            // dev-only, never logged in prod.
            try {
              console.log(`[apiCall] 📤 Enviando body:`, JSON.parse(fetchOptions.body));
            } catch (e) {
              console.log(`[apiCall] 📤 Enviando body (no JSON):`, fetchOptions.body);
            }
          }
        } else {
          console.log(`[apiCall] 📤 Sin body`);
        }
        
        console.log(`[apiCall] ⏳ Iniciando fetch...`);
        console.log(`[apiCall] 🔍 URL completa: ${url}`);
        console.log(`[apiCall] 🔍 FetchOptions keys:`, Object.keys(fetchOptions));
      }
      const fetchStartTime = Date.now();
      
      // Agregar timeout de 120 segundos para evitar que se quede colgado
      // Allow extended timeout for specific endpoints (e.g., propose-flow can take up to 5 minutes)
      // Allow short timeout for quick endpoints (e.g., unread-count should be fast)
      const defaultTimeoutMs = 120000; // 120 segundos (2 minutos)
      const extendedTimeoutMs = authOptions?.extendedTimeout || null;
      const shortTimeoutMs = authOptions?.shortTimeout || null;
      const timeoutMs = extendedTimeoutMs || shortTimeoutMs || defaultTimeoutMs;
      
      if (!isPollingEndpoint || process.env.NODE_ENV === 'development') {
        if (extendedTimeoutMs) {
          console.log(`[apiCall] ⏱️ Using extended timeout: ${timeoutMs}ms (${timeoutMs / 1000}s) for ${endpoint}`);
        } else if (shortTimeoutMs) {
          console.log(`[apiCall] ⏱️ Using short timeout: ${timeoutMs}ms (${timeoutMs / 1000}s) for ${endpoint}`);
        }
      }
      
      let response;
      let bodyText;
      try {
        if (!isPollingEndpoint || process.env.NODE_ENV === 'development') {
          console.log(`[apiCall] 🔄 Ejecutando fetch con timeout de ${timeoutMs}ms...`);
        }
        for (let attempt = 1; ; attempt += 1) {
          try {
            ({ response, bodyText } = await fetchWithDeadline(url, fetchOptions, timeoutMs));
            assertCompleteJsonBody(response, bodyText, url);
            break;
          } catch (transportError) {
            if (effectiveMethod === 'GET' && attempt < GET_TRANSPORT_ATTEMPTS && isRetryableTransportError(transportError)) {
              console.warn(`[apiCall] ↻ ${transportError.transport} on GET ${cleanEndpoint}, retrying (attempt ${attempt + 1}/${GET_TRANSPORT_ATTEMPTS})`);
              continue;
            }
            throw transportError;
          }
        }
        const fetchDuration = Date.now() - fetchStartTime;
        // Only log slow requests or in development for polling endpoints
        if (!isPollingEndpoint || process.env.NODE_ENV === 'development' || fetchDuration > 1000) {
          console.log(`[apiCall] ✅ Fetch completado en ${fetchDuration}ms`);
          console.log(`[apiCall] 📥 Status: ${response.status} ${response.statusText}`);
          if (import.meta.env.DEV) {
            console.log(`[apiCall] 📋 Headers de respuesta:`, Object.fromEntries(response.headers.entries()));
          }
        }
      } catch (fetchError) {
        const fetchDuration = Date.now() - fetchStartTime;
        // Respect silentError for non-critical endpoints (e.g., notifications polling)
        if (!authOptions?.silentError) {
          console.error('='.repeat(80));
          console.error(`[apiCall] ❌ ERROR EN FETCH después de ${fetchDuration}ms`);
          console.error('='.repeat(80));
          console.error(`[apiCall] Error name:`, fetchError.name);
          console.error(`[apiCall] Error message:`, fetchError.message);
          console.error(`[apiCall] Error stack:`, fetchError.stack);
          console.error(`[apiCall] URL:`, url);
          console.error(`[apiCall] Método:`, fetchOptions.method || 'GET');
          console.error(`[apiCall] Backend URL config:`, baseURL);
          console.error('='.repeat(80));
        } else if (process.env.NODE_ENV === 'development') {
          console.debug(`[apiCall] Fetch error (silent): ${fetchError?.message || 'Unknown error'}`);
        }
        throw fetchError;
      }

      // Obtener el tipo de contenido de la respuesta
      const contentType = response.headers.get('content-type');
      let responseData;

      // Manejar la respuesta según el tipo de contenido
      if (response.status === 204) {
        // No Content: devolver objeto estándar para evitar parseo de JSON vacío
        responseData = { status: 204 };
      } else if (contentType && contentType.includes('application/json')) {
        // Algunas respuestas pueden tener cuerpo vacío con content-type json; manejar try/catch
        try {
          const text = bodyText;
          responseData = text ? JSON.parse(text) : {};
          // Transformar fechas en la respuesta usando el interceptor centralizado
          if (responseData && typeof responseData === 'object') {
            responseData = autoTransformDates(responseData);
          }
        } catch (e) {
          console.warn('DEBUG API: JSON vacío o inválido, usando {}');
          responseData = {};
        }
      } else {
        responseData = bodyText;
        console.log('DEBUG API: Respuesta texto:', responseData);
      }

      // Si la respuesta no es exitosa, lanzar un error con los detalles
      // Note: 202 ACCEPTED is considered "ok" (response.ok === true), so async jobs are handled correctly
      if (!response.ok) {
        console.error(`[apiCall] ❌ Respuesta NO exitosa: ${response.status} ${response.statusText}`);
        // Full error body can carry backend-internal details or fragments of
        // another record's data — dev-only, never logged in prod.
        if (import.meta.env.DEV) {
          console.error('='.repeat(80));
          console.error(`[apiCall] 📄 Response data:`, responseData);
          console.error('='.repeat(80));
        }
        const detail = responseData?.detail || response.statusText;
        const normalizedError = buildApiError(response.status, detail, response.statusText, cleanEndpoint);

        // One automatic re-auth retry for stale/expired token errors.
        if (response.status === 401 && !forceReauth && !authOptions.skip401Retry) {
          console.warn(`[apiCall] 401 detected for ${cleanEndpoint}, forcing session refresh and retrying once.`);
          return apiCall(endpoint, options, data, true, {
            ...authOptions,
            skip401Retry: true,
            forceTokenRefresh: true,
          });
        }

        // If 401 persists after forced retry, clear session and redirect.
        if (response.status === 401 && !authOptions.disableAutoLogoutOn401) {
          await handlePersistentUnauthorized(cleanEndpoint, normalizedError);
        }

        // 403 with inactive or expired API key — recoverable auth error.
        // All parallel requests with the same stale key share ONE recovery cycle.
        // This prevents the cascade where each recovery creates and immediately
        // deactivates the previous recovery's key (each calling deactivate_old_session_keys).
        // IMPORTANT: only recover on explicit key errors. Generic 403 (permission denied,
        // impersonated user lacks access, etc.) must NOT trigger session recovery — doing
        // so creates a recovery loop that ends with a false "Session Expired" logout.
        const apiKeyError = responseData?.detail?.error || responseData?.error;
        if (isRecoverableSessionKeyError(response.status, responseData)) {
          if (!authOptions.skip401Retry) {
            console.warn(`[apiCall] 403 (${apiKeyError}) key for ${cleanEndpoint}, coordinating recovery...`);
            const how = await recoverSessionKeyOn403();
            return apiCall(endpoint, options, data, false, {
              ...authOptions,
              skip401Retry: true,
              adoptedKeyRetry: how === 'adopted',
            });
          }
          // The key taken from another tab was refused too (that tab's key was
          // rotated from elsewhere — another browser profile or device). One
          // real exchange before giving up on the session.
          if (authOptions.adoptedKeyRetry) {
            console.warn(`[apiCall] 403 (${apiKeyError}) with the key adopted from another tab for ${cleanEndpoint}, exchanging...`);
            await recoverSessionKeyOn403({ forceExchange: true });
            return apiCall(endpoint, options, data, false, {
              ...authOptions,
              skip401Retry: true,
              adoptedKeyRetry: false,
            });
          }
          // Recovery already attempted (skip401Retry=true) and backend still returns 403.
          // The session is unrecoverable — notify the event bus then redirect to login.
          console.warn(`[apiCall] 403 (${apiKeyError}) persisted after recovery for ${cleanEndpoint}, clearing session.`);
          if (!isPublicRoute()) emitAuthFailure('403_unrecoverable');
          await handlePersistentUnauthorized(cleanEndpoint, normalizedError);
        }

        // 403 account_blocked / org_suspended — structured access-state errors from
        // resolve_user_access_state(), discovered mid-session (not at login — the
        // `auth/gateway` login call sets disableAutoLogoutOn401:true and is handled
        // entirely by AuthContext's own catch block, so it's excluded here to avoid
        // double sign-out/redirect on the same 403).
        if (
          response.status === 403 &&
          (apiKeyError === 'account_blocked' || apiKeyError === 'org_suspended') &&
          !authOptions.disableAutoLogoutOn401
        ) {
          await handleAccessStateError(apiKeyError, cleanEndpoint, normalizedError);
        }

        // Para errores 400, intentar usar el detalle del error si está disponible
        if (response.status === 400 && responseData && responseData.detail) {
          throw buildApiError(response.status, responseData.detail, response.statusText, cleanEndpoint);
        }

        // 428 step_up_required — gated operation needs a fresh MFA-style verification.
        // Read the raw (unflattened) detail here — buildApiError() has already
        // normalized `normalizedError` by this point, so the structured
        // `{error, required_level}` shape must be read straight off responseData.
        if (
          response.status === 428 &&
          responseData?.detail?.error === 'step_up_required' &&
          !authOptions?.skipStepUp
        ) {
          const requiredLevel = responseData.detail.required_level || 2;
          console.warn(`[apiCall] 428 step_up_required (level ${requiredLevel}) for ${cleanEndpoint}, opening step-up modal...`);
          await handleStepUpRequired(requiredLevel);
          return apiCall(endpoint, options, data, false, {
            ...authOptions,
            skipStepUp: true,
          });
        }

        throw normalizedError;
      }
      
      // Log 202 ACCEPTED responses (async jobs) for debugging
      if (response.status === 202) {
        if (!isPollingEndpoint || process.env.NODE_ENV === 'development') {
          console.log(`[apiCall] ⚡ Respuesta 202 ACCEPTED recibida - trabajo asíncrono iniciado`);
          if (responseData?.job_id) {
            console.log(`[apiCall] 📋 Job ID: ${responseData.job_id}, usar /api/jobs/v5/status/${responseData.job_id} para seguir progreso`);
          }
        }
      }

      // Full successful response body can carry financial data, PII, or
      // credentials returned by connector-config endpoints — dev-only, never
      // logged in prod (apiCall() is the universal HTTP client, CLAUDE.md §2).
      if (import.meta.env.DEV) {
        console.log(`[apiCall] ✅ Respuesta exitosa, datos recibidos:`, responseData);
        console.log('='.repeat(80));
      }
      return responseData;
    })();

    if (cacheable) {
      inFlightRequests.set(cacheKey, doRequest);
    }

    if (!isPollingEndpoint || process.env.NODE_ENV === 'development') {
      console.log(`[apiCall] ⏳ Esperando respuesta de doRequest...`);
    }
    let result;
    try {
      result = await doRequest;
    } catch (requestError) {
      if (cacheable && inFlightRequests.get(cacheKey) === doRequest) {
        inFlightRequests.delete(cacheKey);
      }
      throw requestError;
    }
    if (!isPollingEndpoint || process.env.NODE_ENV === 'development') {
      console.log(`[apiCall] ✅ Resultado recibido:`, result);
    }
    if (cacheable) {
      inFlightRequests.delete(cacheKey);
      responseCache.set(cacheKey, { data: result, expiresAt: Date.now() + getTtlMs(cleanEndpoint) });
    }
    return result;
  } catch (error) {
    // Only log errors if silentError is not enabled
    if (!authOptions.silentError) {
      console.error('='.repeat(80));
      console.error('[apiCall] ❌ ERROR CAPTURADO');
      console.error('='.repeat(80));
      console.error('[apiCall] Error message:', error.message);
      console.error('[apiCall] Error stack:', error.stack);
      console.error('[apiCall] Error name:', error.name);
      if (error.response) {
        console.error('[apiCall] Error response:', error.response);
      }
      console.error('='.repeat(80));
    } else {
      // Silent mode: only log at debug level in development
      if (process.env.NODE_ENV === 'development') {
        console.debug('[apiCall] Silent error (handled by caller):', error?.message || error?.name);
      }
    }
    throw error;  // Re-lanzar el error para que sea manejado por el código que llama
  }
};

/**
 * Espera a que la autenticación de Firebase esté disponible
 * @param {number} maxWaitTime - Tiempo máximo de espera en ms (default: 2000ms)
 * @returns {Promise<boolean>} - true si la autenticación está disponible, false si se agotó el tiempo
 */
export const waitForAuthentication = async (maxWaitTime = 2000) => {
  console.log("Esperando a que la autenticación esté disponible...");
  
  const startTime = Date.now();
  
  // Primero intentar obtener currentUser directamente (podría estar ya disponible)
  if (auth.currentUser) {
    console.log("Autenticación ya disponible.");
    return true;
  }
  
  // Si no está disponible, escuchar el evento de cambio de estado de autenticación
  return new Promise(resolve => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      // Limpiar el listener
      unsubscribe();
      
      if (user) {
        console.log(`Autenticación disponible después de ${Date.now() - startTime}ms`);
        resolve(true);
      } else {
        const authData = localStorage.getItem('authData');
        if (authData) {
          console.log("No hay usuario en Firebase pero hay datos en localStorage");
          resolve(true);
        } else {
          console.log("No se detectó autenticación disponible");
          resolve(false);
        }
      }
    });
    
    // Establecer un timeout para no esperar indefinidamente
    setTimeout(() => {
      unsubscribe();
      console.log(`Tiempo de espera agotado después de ${maxWaitTime}ms`);
      const authData = localStorage.getItem('authData');
      if (!authData && !auth.currentUser) {
        // On a public route this condition is not a failure — it is the whole
        // point. "No Firebase user, no localStorage" describes every visitor
        // the public invoice page was built for. Emitting here signed them out
        // and sent them to /firebase-login?session-expired=true a second after
        // the invoice had already rendered (production, 2026-09-04).
        if (isPublicRoute()) {
          console.log('[waitForAuthentication] No session on a public route — expected, not a failure');
        } else {
          console.warn('[waitForAuthentication] No Firebase user, no localStorage — emitting auth failure');
          emitAuthFailure('auth_wait_timeout');
        }
      }
      resolve(!!authData);
    }, maxWaitTime);
  });
};

// Deduplication for forceSyncSession calls
let forceSyncInFlight = null;

// Coordinated 403 inactive/expired key recovery.
// Multiple parallel requests getting the same 403 share ONE recovery cycle —
// preventing the cascade where each recovery deactivates the previous one's key.
let _sessionKeyRecoveryPromise = null;

/**
 * Recover from a stale/rotated session key — extracted so a caller that
 * cannot go through apiCall() (raw fetch(), e.g. ngClient.ts's SSE stream,
 * which fetch() bypasses because EventSource cannot send auth headers) can
 * still use the exact same recovery apiCall() has always had, instead of
 * silently reusing the dead key. Before this existed, that caller had no
 * 403 handling at all: a session-key rotation mid-turn (a normal
 * session/exchange from elsewhere in the app deactivating the key the turn
 * started with) surfaced as "API Key is inactive" narrated by the model as
 * a database connectivity problem, and the user's own retry sent the SAME
 * stale key again because nothing here had refreshed it.
 *
 * Coordinates with apiCall()'s own 403 handling via the SAME
 * `_sessionKeyRecoveryPromise` — a recovery started by one caller is
 * awaited by the other instead of racing two independent recoveries that
 * would each deactivate the previous one's freshly-issued key.
 */
export async function recoverSessionKeyOn403({ forceExchange = false } = {}) {
  // Forced exchanges share their own slot: several calls refused with the
  // same adopted key would otherwise each exchange in turn, every one
  // rotating the key the previous one just obtained.
  if (forceExchange) {
    if (!_forcedExchangePromise) {
      _forcedExchangePromise = (async () => {
        if (_sessionKeyRecoveryPromise) {
          await _sessionKeyRecoveryPromise.catch(() => {});
        }
        return _runSessionKeyRecovery(true);
      })().finally(() => {
        _forcedExchangePromise = null;
      });
    }
    return _forcedExchangePromise;
  }
  if (!_sessionKeyRecoveryPromise) {
    _sessionKeyRecoveryPromise = _runSessionKeyRecovery(false).finally(() => {
      _sessionKeyRecoveryPromise = null;
    });
  }
  return _sessionKeyRecoveryPromise;
}

let _forcedExchangePromise = null;

async function _runSessionKeyRecovery(forceExchange) {
  try {
    const { UserSessionRegistry } = await import('../stores/user-session.registry');
    // A newer key another tab already obtained is used as is: exchanging
    // here would rotate the key every other tab is using.
    if (!forceExchange && UserSessionRegistry.adoptNewerStoredKey()) {
      return 'adopted';
    }
    const recovered = await UserSessionRegistry.recoverSession({ adoptStored: false });
    if (!recovered) {
      await forceSyncSession({ forceTokenRefresh: true });
    }
  } catch (_) {
    localStorage.removeItem('clu_session_key');
    localStorage.removeItem('clu_session_key_expires_at');
    await forceSyncSession({ forceTokenRefresh: true });
  }
  return 'exchanged';
}

/** True when a response body carries the specific "stale session key" shape
 * this recovery applies to — a generic 403 (permission denied, impersonated
 * user lacks access, etc.) must NEVER trigger it, or a real permission
 * error turns into a recovery loop that ends in a false "session expired"
 * logout. Same field-path reading as apiCall()'s own inline check. */
export function isRecoverableSessionKeyError(status, responseData) {
  if (status !== 403) return false;
  const apiKeyError = responseData?.detail?.error || responseData?.error;
  return apiKeyError === 'inactive' || apiKeyError === 'expired';
}

// Coordinated Step-Up Authentication (428) handling.
// Multiple parallel requests hitting the same step-up gate share ONE modal cycle —
// the modal component registers itself here via registerStepUpModalOpener() on mount.
let _stepUpPromise = null;
let _openStepUpModal = null;

export function registerStepUpModalOpener(fn) {
  _openStepUpModal = fn;
}

function handleStepUpRequired(requiredLevel) {
  if (!_stepUpPromise) {
    _stepUpPromise = new Promise((resolve, reject) => {
      if (!_openStepUpModal) {
        reject(new Error('Step-up modal not mounted'));
        return;
      }
      _openStepUpModal({
        level: requiredLevel,
        onVerified: () => resolve(true),
        onCancelled: () => reject(new Error('step_up_cancelled')),
      });
    }).finally(() => {
      _stepUpPromise = null;
    });
  }
  return _stepUpPromise;
}

/**
 * Force-sync the session: refresh Firebase token and validate with backend.
 * Streamlined flow: max ~10s worst case (down from ~65s).
 * @returns {Promise<boolean>} true if session is valid
 */
export const forceSyncSession = async (options = {}) => {
  const { forceTokenRefresh = false } = options;
  const startTime = Date.now();

  // Deduplicate: if a sync is already in progress, wait for it (max 10s)
  if (forceSyncInFlight) {
    try {
      await Promise.race([
        forceSyncInFlight,
        new Promise((_, reject) => setTimeout(() => reject(new Error('forceSyncSession dedup timeout')), 10000)),
      ]);
      return true;
    } catch {
      return false;
    }
  }

  const doSync = async () => {
    // Step 1: Get a fresh Firebase token (the core of the fix)
    let idToken = null;
    const firebaseUser = auth.currentUser;

    if (firebaseUser) {
      try {
        // Only force-refresh when explicitly requested by caller.
        // Forcing refresh on every sync adds avoidable latency and can trigger false timeouts.
        idToken = await Promise.race([
          firebaseUser.getIdToken(!!forceTokenRefresh),
          new Promise((_, reject) => setTimeout(() => reject(new Error('getIdToken timeout')), 8000)),
        ]);
      } catch (tokenError) {
        console.warn('[forceSyncSession] Firebase token refresh failed:', tokenError.message);
      }
    }

    // Step 2: Build user context from UserSessionRegistry (already imported) or localStorage
    let userEmail = null;
    let roleCode = null;
    let orgId = null;

    try {
      const userData = await UserSessionRegistry.getCurrentUser();
      if (userData?.email) {
        userEmail = userData.email;
        roleCode = userData.role || 'user';
        orgId = userData.organizationId || 0;
      }
    } catch {
      // UserSessionRegistry not ready, fall through to localStorage
    }

    // localStorage fallback
    if (!userEmail) {
      try {
        const savedAuthData = JSON.parse(localStorage.getItem('authData') || '{}');
        const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
        userEmail = savedUser.email || savedAuthData.email;
        roleCode = savedAuthData.role_code || 'user';
        orgId = localStorage.getItem('selectedOrganizationId')
          || sessionStorage.getItem('selectedOrganizationId')
          || savedAuthData.organization_id
          || 0;
      } catch {
        // No user data available
      }
    }

    if (!userEmail && !idToken) {
      console.error('[forceSyncSession] No user data or token available');
      return false;
    }

    // Update localStorage with fresh token
    if (idToken) {
      try {
        const authData = JSON.parse(localStorage.getItem('authData') || '{}');
        authData.token = idToken;
        authData.last_updated = new Date().toISOString();
        localStorage.setItem('authData', JSON.stringify(authData));
      } catch { /* non-critical */ }
    }

    // Step 3: Validate session with auth/status (lightweight check)
    const headers = {
      'Content-Type': 'application/json',
    };
    if (userEmail) headers['X-User-Email'] = userEmail;
    if (roleCode) headers['X-User-Role'] = roleCode;
    if (orgId) headers['X-User-Org'] = String(orgId);
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;

    try {
      const response = await Promise.race([
        fetch(`${baseURL}/api/auth/status`, { method: 'GET', credentials: 'include', headers }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('auth/status timeout')), 15000)),
      ]);

      if (!response.ok) {
        console.warn(`[forceSyncSession] auth/status returned ${response.status}`);
        return false;
      }

      const result = await response.json();
      if (!result?.authenticated) {
        console.warn('[forceSyncSession] Session not authenticated');
        return false;
      }

      const elapsed = Date.now() - startTime;
      console.log(`[forceSyncSession] Session validated in ${elapsed}ms`);
      localStorage.setItem('localSessionEstablished', 'true');
      localStorage.setItem('lastSyncTime', new Date().toISOString());
      return true;
    } catch (fetchError) {
      const errorMessage = String(fetchError?.message || '');
      // Availability-first fallback: if backend auth/status times out but we have
      // a fresh Firebase token, allow the request path to continue and rely on
      // normal backend auth validation on the actual API call.
      if (errorMessage.includes('auth/status timeout') && idToken) {
        console.warn('[forceSyncSession] auth/status timeout; continuing with token-only fallback.');
        localStorage.setItem('localSessionEstablished', 'true');
        localStorage.setItem('lastSyncTime', new Date().toISOString());
        return true;
      }
      console.error('[forceSyncSession] Validation failed:', errorMessage);
      return false;
    }
  };

  try {
    forceSyncInFlight = doSync();
    return await forceSyncInFlight;
  } finally {
    forceSyncInFlight = null;
  }
};

/**
 * Maneja un cambio de contexto de organización, actualizando authData y cookies
 * @param {number} organizationId - ID de la nueva organización
 * @returns {Promise<boolean>} - true si se actualizó correctamente
 */
export const handleOrganizationChange = async (organizationId) => {
  try {
    if (!organizationId) return false;
    
    console.log(`API Service: Detectado cambio de organización a ${organizationId}`);
    
    // Obtener los datos actuales de autenticación
    const authData = JSON.parse(localStorage.getItem('authData') || '{}');
    const currentOrgId = authData.organization_id;
    
    // Si no hay cambio, no hacer nada
    if (currentOrgId === organizationId) {
      return true;
    }
    
    console.log(`API Service: Actualizando contexto de organización: ${currentOrgId} -> ${organizationId}`);
    
    try {
      // Importar UserService de forma dinámica para evitar dependencias circulares
      const UserService = (await import('./user.service')).default;
      
      // Llamar al método específico para refrescar el contexto
      await UserService.refreshOrganizationContext(organizationId);
      return true;
    } catch (userServiceError) {
      console.error("Error importando UserService:", userServiceError);
      
      // Actualizar manualmente authData como fallback
      authData.organization_id = organizationId;
      localStorage.setItem('authData', JSON.stringify(authData));
      
      // Intentar llamar directamente al endpoint
      try {
        await fetch(`${baseURL}/api/auth/switch-organization`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authData.token ? `Bearer ${authData.token}` : undefined,
            'X-User-Role': authData.role_code || '',
            'X-User-Org': authData.organization_id ? authData.organization_id.toString() : '',
            'X-User-Email': authData.email || ''
          },
          body: JSON.stringify({ organization_id: organizationId })
        });
        return true;
      } catch (fetchError) {
        console.warn("Error actualizando contexto via endpoint:", fetchError);
        return false;
      }
    }
  } catch (error) {
    console.error("Error general en handleOrganizationChange:", error);
    return false;
  }
};

/**
 * Detecta si una llamada API requiere un cambio de contexto de organización y lo maneja
 * @param {string} endpoint - Endpoint de la API
 * @param {string} method - El método HTTP para la llamada
 * @returns {Promise<boolean>} - true si se detectó y manejó un cambio de organización
 */
export const detectOrganizationChange = async (endpoint, method) => {
  try {
    // Detectar si el endpoint contiene un parámetro organization_id
    if (endpoint.includes('organization_id=')) {
      const match = endpoint.match(/organization_id=(\d+)/);
      if (match && match[1]) {
        const organizationId = parseInt(match[1]);
        
        // Verificar si es un cambio respecto al contexto actual
        const authData = JSON.parse(localStorage.getItem('authData') || '{}');
        const currentOrgId = authData.organization_id;
        
        if (organizationId !== currentOrgId) {
          // Actualizar el contexto
          return await handleOrganizationChange(organizationId);
        }
      }
    }
    
    // También detectar en el cuerpo de las solicitudes POST/PUT
    if (method === 'POST' || method === 'PUT') {
      const body = await fetch(endpoint, { method: 'OPTIONS' }).then(response => response.json());
      if (body.organization_id) {
        const organizationId = body.organization_id;
        
        // Verificar si es un cambio respecto al contexto actual
        const authData = JSON.parse(localStorage.getItem('authData') || '{}');
        const currentOrgId = authData.organization_id;
        
        if (organizationId !== currentOrgId) {
          // Actualizar el contexto
          return await handleOrganizationChange(organizationId);
        }
      }
    }
    
    return false;
  } catch (error) {
    console.error("Error en detectOrganizationChange:", error);
    return false;
  }
};

/**
 * Realiza una llamada a la API con reintentos automáticos de autenticación
 * Esta función es más robusta que apiCall para casos donde la autenticación puede fallar
 * @param {string} endpoint - El endpoint a llamar (sin /api/)
 * @param {string} method - Método HTTP ('GET', 'POST', etc)
 * @param {Object} data - Datos a enviar (opcional)
 * @param {Object} options - Opciones adicionales
 * @returns {Promise<any>} - La respuesta de la API
 */
export const fetchAuthRetry = async (endpoint, method = 'GET', data = null, options = {}) => {
  const maxRetries = 2;
  let lastError = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`fetchAuthRetry: Intento ${attempt + 1}/${maxRetries + 1} para ${method} ${endpoint}`);
      
      // En el primer intento, usar autenticación normal
      // En reintentos, forzar reautenticación
      const forceReauth = attempt > 0;
      const authOptions = {
        forceWaitAuth: attempt > 0, // Esperar autenticación en reintentos
        ...options
      };
      
      const response = await apiCall(endpoint, method, data, forceReauth, authOptions);
      
      console.log(`fetchAuthRetry: Éxito en intento ${attempt + 1} para ${endpoint}`);
      return response;
      
    } catch (error) {
      lastError = error;
      console.warn(`fetchAuthRetry: Error en intento ${attempt + 1} para ${endpoint}:`, error);
      
      // Si es un error de autenticación (401) y no es el último intento, continuar
      if (isAuthUnauthorizedError(error) && attempt < maxRetries) {
        console.log(`fetchAuthRetry: Error de autenticación detectado, reintentando...`);
        
        // Esperar un poco antes del siguiente intento
        await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
        
        // Forzar sincronización de sesión antes del siguiente intento
        try {
          await forceSyncSession({ forceTokenRefresh: true });
        } catch (syncError) {
          console.warn('Error en forceSyncSession:', syncError);
        }
        
        continue;
      }
      
      // Si no es un error de autenticación o es el último intento, lanzar el error
      break;
    }
  }
  
  console.error(`fetchAuthRetry: Todos los intentos fallaron para ${endpoint}`, lastError);
  if (isAuthUnauthorizedError(lastError) && !options.disableAutoLogoutOn401) {
    await handlePersistentUnauthorized(endpoint, lastError);
  }
  throw lastError;
};

/**
 * Realiza una llamada a un endpoint ROOT (sin /api ni baseURL), útil para rutas como /rag-advanced/columns
 * @param {string} endpoint - El endpoint a llamar (debe empezar por /)
 * @param {Object|string} options - Opciones para la llamada fetch, o método HTTP como string ('GET', 'POST', etc)
 * @param {Object} data - Datos a enviar (solo si options es un string representando el método)
 * @param {boolean} forceReauth - Si debe forzar la reautenticación antes de la llamada
 * @param {Object} authOptions - Opciones adicionales de autenticación
 * @returns {Promise<any>} - La respuesta de la API
 */
export const apiCallRoot = async (endpoint, options = {}, data = null, forceReauth = false, authOptions = {}) => {
  // Detectar si options es un string (método HTTP)
  if (typeof options === 'string') {
    const method = options.toUpperCase();
    const newOptions = { method, headers: {} };
    if (data !== null && method !== 'GET') {
      if (authOptions.isFormData || data instanceof FormData) {
        newOptions.body = data;
      } else {
        newOptions.body = JSON.stringify(data);
      }
    }
    options = newOptions;
  }
  // Configuración predeterminada
  const defaultOptions = {
    method: options.method || 'GET',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
    }
  };
  if (!(options.body instanceof FormData) && !authOptions.isFormData) {
    defaultOptions.headers['Content-Type'] = 'application/json';
  }
  if (options.body) {
    defaultOptions.body = options.body;
  }
  // Añadir headers de autenticación usando UserSessionRegistry (NUEVA ARQUITECTURA - NO localStorage)
  try {
    const { UserSessionRegistry } = await import('../stores/user-session.registry');
    
    // Obtener organización activa desde UserSessionRegistry
    let organizationId = null;
    try {
      const userData = await UserSessionRegistry.getCurrentUser();
      organizationId = userData?.organizationId;
    } catch (e) {
      console.warn('[apiCallRoot] Could not get organizationId from UserSessionRegistry:', e);
    }
    
    // Obtener headers de autenticación desde UserSessionRegistry (fuente de verdad)
    const authHeaders = await UserSessionRegistry.getAuthHeaders({
      orgId: organizationId ? Number(organizationId) : undefined,
      includeToken: true,
    });
    
    // Aplicar headers de autenticación
    Object.assign(defaultOptions.headers, authHeaders);
  } catch (error) {
    console.error('[apiCallRoot] CRITICAL: Could not use UserSessionRegistry:', error);
    // NO FALLBACK to localStorage - UserSessionRegistry is required
    // If it fails, we cannot authenticate properly
    // The request will proceed without auth headers and may fail with 401
    // This is intentional - we want to fail fast rather than use stale data
  }
  // Construir URL absoluta al backend si el endpoint es relativo (empieza con /)
  const url = /^(https?:)?\/\//.test(endpoint) ? endpoint : `${baseURL}${endpoint}`;
  return fetch(url, defaultOptions).then(async res => {
    if (!res.ok) {
      let errorText = await res.text();
      throw new Error(`API Error: ${res.status} - ${errorText}`);
    }
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return res.json();
    } else if (contentType && contentType.includes('text/')) {
      return res.text();
    } else {
      return res;
    }
  });
};

export default {
  apiCall,
  forceSyncSession
}; 